import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { toast } from "sonner";
import { Loader2, Mail, User as UserIcon, Sparkles } from "lucide-react";

const schema = z.object({
  email: z.string().trim().email("Invalid email").max(255),
  name: z.string().trim().max(80).optional(),
  interest: z.string().trim().max(80).optional(),
});

const INTERESTS = ["Find Work", "Hire Talent", "Build Business", "Creator", "Just curious"];

const Waitlist = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [interest, setInterest] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email, name: name || undefined, interest: interest || undefined });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("waitlist").insert({
      email: parsed.data.email.toLowerCase(),
      name: parsed.data.name ?? null,
      interest: parsed.data.interest ?? null,
      source: "landing",
    });
    setLoading(false);
    if (error) {
      if (error.code === "23505") {
        setDone(true);
        toast.success("You're already on the list! 🌴");
        return;
      }
      toast.error(error.message);
      return;
    }
    setDone(true);
    toast.success("You're on the list! 🎉");
  };

  return (
    <MobileShell>
      <Helmet>
        <title>Join the Cash Isle waitlist</title>
        <meta name="description" content="Be among the first to use Cash Isle — the Bahamas' financial super app for sending money, hiring talent, and growing your hustle." />
        <link rel="canonical" href="https://cash-isle-hustle-hub.lovable.app/waitlist" />
        <meta property="og:title" content="Join the Cash Isle waitlist" />
        <meta property="og:description" content="Be among the first to use Cash Isle — the Bahamas' financial super app." />
        <meta property="og:url" content="https://cash-isle-hustle-hub.lovable.app/waitlist" />
      </Helmet>
      <BrandHeader onBack={() => navigate("/")} curve />
      <div className="-mt-6 mx-4 bg-background rounded-2xl shadow-card px-6 pt-7 pb-8 flex-1 animate-fade-in-up">
        <div className="flex justify-center mb-3">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center">
            <Sparkles className="h-7 w-7 text-primary" />
          </div>
        </div>
        <h1 className="font-display text-3xl font-bold text-center mb-2">Join the Waitlist</h1>
        <p className="text-center text-muted-foreground text-sm mb-6">
          Find Work. Build Business. Get Paid. Be first when we open the gates.
        </p>

        {done ? (
          <div className="space-y-4 text-center">
            <div className="p-4 bg-primary/5 rounded-xl text-sm">
              Thanks{name ? `, ${name}` : ""}! We'll email <strong>{email}</strong> the moment your spot is ready.
            </div>
            <Button variant="hero" size="xl" className="w-full" onClick={() => navigate("/")}>
              Back to home
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 pl-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2 px-1">I'm here to…</p>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setInterest(interest === opt ? "" : opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      interest === opt
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-secondary/60 border-transparent text-foreground"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" variant="hero" size="xl" className="w-full mt-4" disabled={loading}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reserve my spot"}
            </Button>
          </form>
        )}
      </div>
    </MobileShell>
  );
};

export default Waitlist;
